import React, {type ReactNode, useRef} from 'react';

import {HighlightableTextWrapper} from 'app/comments/components/HighlightableTextWrapper';
import {ClickToEditButton} from 'app/components/general/ClickToEditButton';
import {DocumentFoldersList} from 'app/folders/components/DocumentFoldersList';
import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import {DocumentMetadataStatusButtons} from 'app/general/structures/DocumentMetadataStatusButtons';
import MetaContentTypeName from 'app/general/structures/MetaContentTypeName';
import {HIGHLIGHT_POLICY_TITLE} from 'app/general/structures/UserContentContainer/utils/highlightUtils';
import {ManagersList} from 'app/procedures/components/ViewProcedureMetaData/ManagersList';
import {ModifiedCreatedDataStamp} from 'app/procedures/components/ViewProcedureMetaData/ModifiedCreatedDataStamp';
import {DocumentTagsList} from 'app/tags/components/DocumentTagsList';
import {getPolicyName} from 'app/utils/fallbacks';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import type {PolicyContentWithDiffType} from 'app/policies/types';
import type {DetailPolicySerializedType} from 'sweet-types';

type Props = {
  advancedModeEnabled?: boolean;
  warningComponent?: ReactNode;
  policyContent: PolicyContentWithDiffType;
  policy: DetailPolicySerializedType;
  versionId: number;
  editableEmptyDataMessage?: string | null;
  editMode?: boolean;
};

export const ViewPolicyMetadata = ({
  advancedModeEnabled,
  warningComponent,
  policy,
  policyContent,
  editableEmptyDataMessage,
  editMode,
  versionId,
}: Props) => {
  const hasNoTags = policy.tags.length === 0;
  const contentRef = useRef<HTMLDivElement | null>(null);
  const policyTitle =
    typeof policyContent.name === 'string'
      ? getPolicyName(policyContent, true)
      : policyContent.name;

  return (
    <DocHeaderStructure
      thumbnail={policyContent.thumbnail}
      warningContent={warningComponent}
      advancedContent={
        <>
          {advancedModeEnabled === true ? (
            <div className="row">
              <div className="col">
                <ModifiedCreatedDataStamp document={policy} />
                {policy.managers != null ? (
                  <ManagersList document={policy} />
                ) : null}
              </div>
            </div>
          ) : null}
          <MetaContentTypeName
            contentType="policy"
            statusContent={
              <DocumentMetadataStatusButtons
                canEdit={policy.can_edit}
                contentType="policy"
                context={'view'}
                document={policy}
              />
            }
          />
        </>
      }
      tagsContent={
        <>
          {hasNoTags && editableEmptyDataMessage != null ? (
            <div className="form-group">
              <span className="small text-secondary font-weight-light">
                {editableEmptyDataMessage}
              </span>
            </div>
          ) : (
            <DocumentTagsList tags={policy.tags} />
          )}
          <DocumentFoldersList connections={policy.connections} />
        </>
      }
      titleContent={
        <h1
          className={`fr-view ${
            isRightToLeftWrapper(policyContent.name) ? 'right-to-left-text' : ''
          }`}
          ref={contentRef}
        >
          {editMode ? (
            <>
              {policyTitle}
              <ClickToEditButton />
            </>
          ) : (
            <HighlightableTextWrapper
              objectId={policy.id}
              versionId={versionId}
              stepId={HIGHLIGHT_POLICY_TITLE}
              content={policyTitle}
              contentType={'policyversion'}
              innerRef={contentRef}
            />
          )}
        </h1>
      }
    />
  );
};
